import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Clock, FileText, Loader2, Send } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import type { WindowState } from '@/lib/window';
import { useT } from '@/lib/i18n';

// Janela de 24 horas do WhatsApp oficial, na conversa aberta do Inbox.
//
// A regra é da Meta: texto livre só chega a quem escreveu nas últimas 24 horas;
// fora disso, só template aprovado. A API já resolve sozinha quando há template
// de alternativa — aqui o atendente VÊ o que vai acontecer antes de apertar
// enviar, em vez de descobrir pelo erro.

interface Tpl {
  id: string; name: string; language: string; category: string; status: string;
  variables: number; spec?: { body?: { text?: string }; header?: { type?: string; text?: string } };
}

function fmtLeft(ms: number): string {
  const min = Math.ceil(ms / 60_000);
  const h = Math.floor(min / 60);
  return h > 0 ? `${h}h${String(min % 60).padStart(2, '0')}` : `${min}min`;
}

/** Aviso fino acima do compositor. */
export function WindowNotice({ state, fallbackName }: { state: WindowState; fallbackName?: string }) {
  const t = useT();
  if (state.open) {
    return (
      <p className="mb-1.5 flex items-center gap-1.5 text-[11px] text-emerald-700 dark:text-emerald-400">
        <Clock className="size-3" /> {t('inbox.window.open', { time: fmtLeft(state.leftMs) })}
      </p>
    );
  }
  const head = state.never ? t('inbox.window.never') : t('inbox.window.closed');
  return (
    <p className="mb-1.5 flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-400">
      <Clock className="mt-px size-3 shrink-0" />
      <span>
        {head}{' '}
        {fallbackName ? t('inbox.window.viaFallback', { name: fallbackName }) : t('inbox.window.pickTemplate')}
      </span>
    </p>
  );
}

/** Escolher e enviar um template aprovado nesta conversa. */
export function TemplateSender({ to, instanceId, onSent }: { to: string; instanceId?: string; onSent?: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const { data: list = [], isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => ((await apiFetch('GET', '/templates')).data as { templates?: Tpl[] })?.templates ?? [],
  });
  const approved = list.filter((x) => x.status === 'APPROVED');
  const [id, setId] = useState('');
  const [vars, setVars] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const tpl = approved.find((x) => x.id === id);
  const isAuth = tpl?.category === 'AUTHENTICATION';

  const pick = (next: string) => {
    setId(next);
    const n = approved.find((x) => x.id === next)?.variables ?? 0;
    setVars(Array.from({ length: n }, () => ''));
    setCode('');
  };

  const ready = !!tpl && (isAuth ? code.trim() !== '' : vars.every((v) => v.trim() !== ''));

  const send = async () => {
    if (!tpl || !ready) return;
    setSending(true);
    try {
      const body: Record<string, unknown> = { to, template: tpl.name, language: tpl.language };
      if (instanceId) body.instance_id = instanceId;
      if (isAuth) body.code = code.trim();
      else if (vars.length) body.variables = vars.map((v) => v.trim());
      const r = await apiFetch('POST', '/messages/template', body);
      if (!r.ok) {
        toast.error((r.data as { message?: string } | null)?.message ?? t('inbox.window.sendFailed'));
        return;
      }
      toast.success(t('inbox.window.sent'));
      pick('');
      void qc.invalidateQueries({ queryKey: ['history', to] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      onSent?.();
    } finally {
      setSending(false);
    }
  };

  if (isLoading) return <Loader2 className="mx-auto my-2 size-4 animate-spin text-black/40" />;
  if (approved.length === 0) {
    return <p className="py-1 text-xs text-black/60 dark:text-white/60">{t('inbox.window.noTemplates')}</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <FileText className="size-4 shrink-0 text-black/55 dark:text-white/60" />
        <select
          value={id}
          onChange={(e) => pick(e.target.value)}
          aria-label={t('inbox.window.choose')}
          className="h-9 flex-1 rounded-lg border-0 bg-white px-2 text-sm text-[#111b21] outline-none dark:bg-[#2a3942] dark:text-[#e9edef]"
        >
          <option value="">{t('inbox.window.choose')}</option>
          {approved.map((x) => <option key={x.id} value={x.id}>{x.name} · {x.language}</option>)}
        </select>
      </div>
      {tpl?.spec?.body?.text && (
        <p className="whitespace-pre-wrap rounded-md bg-white/70 px-2.5 py-1.5 text-xs text-black/70 dark:bg-white/10 dark:text-white/70">{tpl.spec.body.text}</p>
      )}
      {tpl && isAuth && (
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t('inbox.window.code')}
          className="h-9 rounded-lg border-0 bg-white px-3 text-sm outline-none dark:bg-[#2a3942] dark:text-[#e9edef]" />
      )}
      {tpl && !isAuth && vars.map((v, i) => (
        <input key={i} value={v} onChange={(e) => setVars((cur) => cur.map((x, j) => (j === i ? e.target.value : x)))}
          placeholder={t('inbox.window.variable', { n: i + 1 })}
          className="h-9 rounded-lg border-0 bg-white px-3 text-sm outline-none dark:bg-[#2a3942] dark:text-[#e9edef]" />
      ))}
      {tpl && (
        <button
          onClick={() => void send()}
          disabled={!ready || sending}
          className="flex items-center justify-center gap-2 self-end rounded-full bg-[#008069] px-4 py-2 text-sm text-white transition-opacity hover:opacity-90 disabled:opacity-40 dark:bg-[#00a884]"
        >
          {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          {t('inbox.window.send')}
        </button>
      )}
    </div>
  );
}
